import React, { useEffect, useMemo, useState } from "react";
import { BACKEND_URL } from "../config";

export default function TrainerDashboard({ token, authUser, showToast }) {
  const [loading, setLoading] = useState(true);
  const [clients, setClients] = useState([]);
  const [error, setError] = useState("");

  // message modal state
  const [showMsg, setShowMsg] = useState(false);
  const [msgClient, setMsgClient] = useState(null);
  const [msgSubject, setMsgSubject] = useState("");
  const [msgBody, setMsgBody] = useState("");
  const [sending, setSending] = useState(false);

  const isTrainerOrAdmin = !!authUser?.is_trainer || !!authUser?.is_admin;

  const loadClients = async () => {
    setError("");
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/trainer/clients`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || "Failed to load clients");
      }

      const data = await res.json();
      setClients(data.clients || []);
    } catch (e) {
      setError(e.message || "Failed to load clients");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) loadClients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const displayClients = useMemo(() => {
    // Normalize just in case any shape changes
    return (clients || [])
      .map((u) => (u?.auth_info ? u.auth_info : u))
      .filter((u) => u && u.user_id);
  }, [clients]);

  const openMessageModal = (client) => {
    setMsgClient(client);
    setMsgSubject("");
    setMsgBody("");
    setShowMsg(true);
  };

  const closeMessageModal = () => {
    if (sending) return;
    setShowMsg(false);
    setMsgClient(null);
  };

  const sendMessage = async () => {
    if (!msgClient?.user_id) return;
    if (!msgBody.trim()) {
      showToast?.("Message is required.", "error");
      return;
    }

    setSending(true);
    try {
      const payload = {
        from_user_id: authUser.user_id,
        to_user_id: msgClient.user_id,
        subject: msgSubject?.trim() || null,
        message: msgBody.trim(),
      };

      const res = await fetch(`${BACKEND_URL}/api/messages/send`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || "Failed to send message");
      }

      showToast?.("Message sent!");
      closeMessageModal();
    } catch (e) {
      showToast?.("Error sending message: " + (e.message || "Unknown error", "error"));
    } finally {
      setSending(false);
    }
  };

  if (!isTrainerOrAdmin) {
    return (
      <div className="container">
        <div className="card" style={{ padding: 24 }}>
          <h1 className="heading-3" style={{ marginBottom: 8 }}>
            Trainer Dashboard
          </h1>
          <p className="body-medium" style={{ color: "var(--text-secondary)" }}>
            You don’t have trainer access.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="container" data-testid="trainer-dashboard">
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <div>
          <h1 className="heading-2" style={{ marginBottom: 6 }}>
            Trainer Dashboard
          </h1>
          <p className="body-medium" style={{ color: "var(--text-secondary)" }}>
            Manage your clients and send messages.
          </p>
        </div>

        <button className="btn-secondary" onClick={loadClients} disabled={loading}>
          {loading ? "Loading…" : "Refresh"}
        </button>
      </div>

      {error && (
        <div className="card" style={{ marginTop: 16, padding: 16, borderColor: "#ef4444" }}>
          <p className="body-medium" style={{ color: "#b91c1c" }}>
            {error}
          </p>
        </div>
      )}

      <div className="card" style={{ marginTop: 20, padding: 20 }}>
        <h2 className="heading-3" style={{ marginBottom: 14 }}>
          Clients ({displayClients.length})
        </h2>

        {loading ? (
          <p className="body-medium">Loading…</p>
        ) : displayClients.length === 0 ? (
          <p className="body-medium" style={{ color: "var(--text-secondary)" }}>
            No clients assigned yet.
          </p>
        ) : (
          <div
            className="admin-users-grid"
            style={{
              // reuse your grid style, but make sure it works here too
              marginTop: 0,
            }}
          >
            {displayClients.map((c) => (
              <div key={c.user_id} className="admin-user-card">
                <div className="admin-user-header">
                  <h3 className="heading-4" style={{ margin: 0 }}>
                    {c.name || "Unnamed"}
                  </h3>
                  {c.is_admin && <span className="admin-badge">Admin</span>}
                </div>

                <p className="body-small" style={{ marginTop: 6 }}>
                  {c.email || ""}
                </p>

                <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
                  <button
                    className="btn-primary"
                    style={{ flex: 1 }}
                    onClick={() => openMessageModal(c)}
                  >
                    ✉️ Message
                  </button>

                  {/* Placeholder buttons for future trainer actions */}
                  <button
                    className="btn-secondary"
                    style={{ flex: 1 }}
                    onClick={() => showToast?.("Next: open client workspace (goals/habits/calendar/coach rules).", "info")}
                  >
                    Open Client
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Message Modal */}
      {showMsg && msgClient && (
        <div
          className="modal-overlay"
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.75)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 2000,
            padding: 16,
          }}
          onMouseDown={closeMessageModal}
        >
          <div
            className="modal-card"
            style={{
              background: "#fff",
              color: "#111827",
              width: "100%",
              maxWidth: 640,
              borderRadius: 16,
              boxShadow: "0 30px 80px rgba(0,0,0,0.35)",
              overflow: "hidden",
            }}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div
              className="modal-header"
              style={{
                padding: "16px 18px",
                borderBottom: "1px solid rgba(0,0,0,0.08)",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 12,
              }}
            >
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 18, fontWeight: 800, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  Message — {msgClient.name || msgClient.email || msgClient.user_id}
                </div>
                <div style={{ fontSize: 13, color: "#6B7280" }}>
                  To: {msgClient.email || msgClient.user_id}
                </div>
              </div>

              <button
                type="button"
                className="btn-secondary"
                onClick={closeMessageModal}
                disabled={sending}
                style={{ minHeight: 40, padding: "10px 14px" }}
              >
                Close
              </button>
            </div>

            <div className="modal-body" style={{ padding: 18 }}>
              <div className="form-group">
                <label className="body-medium">Subject (optional)</label>
                <input
                  className="form-input"
                  value={msgSubject}
                  onChange={(e) => setMsgSubject(e.target.value)}
                  placeholder="e.g., Weekly check-in"
                />
              </div>

              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="body-medium">Message</label>
                <textarea
                  className="form-input"
                  rows={6}
                  value={msgBody}
                  onChange={(e) => setMsgBody(e.target.value)}
                  placeholder="Write your message…"
                  style={{ resize: "vertical" }}
                />
              </div>
            </div>

            <div
              className="modal-footer"
              style={{
                padding: "16px 18px",
                borderTop: "1px solid rgba(0,0,0,0.08)",
                display: "flex",
                justifyContent: "flex-end",
                gap: 10,
              }}
            >
              <button className="btn-secondary" onClick={closeMessageModal} disabled={sending}>
                Cancel
              </button>
              <button className="btn-primary" onClick={sendMessage} disabled={sending}>
                {sending ? "Sending…" : "Send"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
